import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { getIcon } from '../../data/iconMap';
import { CATEGORIES, CATEGORY_ORDER, categoryUrl } from '../../data/categories';

/**
 * Desktop mega-menu navigation.
 * Hover or keyboard focus opens a full-width panel with subcategories.
 */
export default function MegaMenu({ onOpenChange }) {
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (onOpenChange) onOpenChange(!!openKey);
  }, [openKey, onOpenChange]);

  const openPanel = (key) => {
    clearTimeout(closeTimer.current);
    setOpenKey(key);
  };
  const schedulePanelClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenKey(null), 120);
  };

  return (
    <div className="flex items-center gap-2" data-testid="mega-menu-root">
      {CATEGORY_ORDER.map((key) => {
        const cat = CATEGORIES[key];
        const isOpen = openKey === key;
        return (
          <div
            key={key}
            className="relative"
            onMouseEnter={() => openPanel(key)}
            onMouseLeave={schedulePanelClose}
          >
            <Link
              to={categoryUrl(cat.slug)}
              className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                isOpen ? 'text-gold' : 'text-white/80 hover:text-gold'
              }`}
              data-testid={`nav-${cat.slug}`}
              onFocus={() => openPanel(key)}
            >
              {cat.short}
              <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </Link>

            {isOpen && (
              <MegaPanel
                category={cat}
                onMouseEnter={() => openPanel(key)}
                onMouseLeave={schedulePanelClose}
                onNavigate={() => setOpenKey(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function MegaPanel({ category, onMouseEnter, onMouseLeave, onNavigate }) {
  const HeroIcon = getIcon(category.icon);
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="fixed left-0 right-0 top-[88px] z-40 bg-[#050C16]/98 border-t border-b border-gold/20 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-top-2 duration-200"
      style={{ backgroundColor: 'rgba(5, 12, 22, 0.98)', backdropFilter: 'blur(24px)' }}
      data-testid={`mega-panel-${category.slug}`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div
          className="animate-in fade-in duration-200"
        >
          <div className="grid grid-cols-12 gap-8 p-8 lg:p-10">
            {/* Left: big category card */}
            <Link
              to={categoryUrl(category.slug)}
              onClick={onNavigate}
              className="col-span-4 relative h-full min-h-[260px] overflow-hidden group"
              data-testid={`mega-hero-${category.slug}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url('${category.hero}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/60 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-7">
                <HeroIcon className="text-gold mb-3" size={22} />
                <h3
                  className="text-2xl font-medium mb-2 text-white"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {category.title}
                </h3>
                <p className="text-xs text-white/60 mb-4 leading-relaxed">{category.tagline}</p>
                <span className="text-xs uppercase tracking-[0.25em] text-gold group-hover:translate-x-1 transition-transform inline-flex items-center gap-2">
                  Explore <ArrowRight size={14} />
                </span>
              </div>
            </Link>

            {/* Right: subcategory grid */}
            <div className="col-span-8 grid grid-cols-3 gap-x-8 gap-y-5">
              {category.subcategories.map((sub) => {
                const SubIcon = getIcon(sub.icon);
                const href = sub.external ? sub.slug : categoryUrl(category.slug, sub.slug);
                return (
                  <Link
                    key={sub.slug}
                    to={href}
                    onClick={onNavigate}
                    className="group flex items-start gap-3 py-1"
                    data-testid={`mega-sub-${category.slug}-${sub.slug.replace(/\//g, '-')}`}
                  >
                    <span className="flex-shrink-0 w-9 h-9 flex items-center justify-center border border-white/10 group-hover:border-gold/60 group-hover:text-gold text-white/70 transition-colors">
                      <SubIcon size={16} />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm text-white group-hover:text-gold transition-colors">
                        {sub.name}
                      </span>
                      {sub.description && (
                        <span className="text-xs text-white/40 mt-0.5 leading-snug">
                          {sub.description}
                        </span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Footer strip */}
          <div className="border-t border-white/10 px-8 lg:px-10 py-4 flex items-center justify-between text-xs text-white/50">
            <span className="uppercase tracking-[0.2em]">{category.description}</span>
            <Link
              to="/list/new"
              onClick={onNavigate}
              className="text-gold uppercase tracking-[0.2em] hover:text-[#E5C55D] transition-colors"
              data-testid={`mega-list-cta-${category.slug}`}
            >
              List with us →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
